// M21b "Generic" family (owner brief 2026-10-12): 8 neutral templates for any restaurant and any food — café,
// pizza, sushi, bakery, juice bar. White or black, the restaurant's primary colour as the one accent, and My
// brand's own heading and body fonts (owner: Ideas keep their fixed display fonts, Generic uses the brand's).
// Same rules as M20 (tests/unit/templates.test.ts).
import type { Builder, TemplateDef, TextOpts } from './dsl';
import { esOnly } from '../ad/fields';
import { SOFT } from './ideas';

const bi = (es: string, en: string) => ({ es, en });
const INK = '#1a1a1a';
const MUTED = '#6b6b6b';

type G = Omit<TextOpts, 'role' | 'family' | 'font'> & { font?: 'heading' | 'body' };
/** A Spanish ad text in My brand's heading (default) or body font. */
const gx = (b: Builder, role: TextOpts['role'], x: number, y: number, w: number, h: number, o: G) =>
  b.text(x, y, w, h, {
    maxLines: 1,
    font: 'heading',
    weight: (o.font ?? 'heading') === 'heading' ? 700 : 400,
    ...o,
    role,
    lang: 'es',
  });

const shadeBlack = (b: Builder, x: number, y: number, w: number, h: number, dir: 'down' | 'right' = 'down') =>
  b.grad(
    x,
    y,
    w,
    h,
    [
      [0, ['black', dir === 'down' ? 0 : 1]],
      [0.6, ['black', dir === 'down' ? 0.6 : 0.4]],
      [1, ['black', dir === 'down' ? 0.85 : 0]],
    ],
    dir,
  );

export const GENERIC_TEMPLATES: TemplateDef[] = [
  {
    id: 'generic-post',
    family: 'generic',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Genérico — publicación', 'Generic — post'),
    build: (b) => {
      b.bg('white');
      b.photo(90, 60, 900, 740, { fit: 'contain', zoom: 0.94, shadow: SOFT });
      // A long name wraps to two lines (the M14 long-name check).
      gx(b, 'dishName', 90, 815, 620, 140, { size: 60, color: INK, maxLines: 2, leading: 1.05 });
      gx(b, 'price', 720, 830, 270, 90, { size: 64, color: 'accentText', align: 'right' });
      b.rect(90, 962, 900, 4, { fill: 'primary' });
      b.logo(90, 975, 100, 100, { onLight: true });
    },
  },
  {
    id: 'generic-portrait',
    family: 'generic',
    slot: 'full',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Genérico — foto completa vertical', 'Generic — full photo portrait'),
    build: (b) => {
      b.bg('black');
      b.photo(0, 0, 1080, 980);
      b.rect(70, 1010, 120, 6, { fill: 'primary' });
      gx(b, 'dishName', 70, 1035, 940, 90, { size: 72, color: 'white' });
      gx(b, 'description', 70, 1130, 940, 50, { font: 'body', size: 38, color: ['white', 0.85] });
      gx(b, 'price', 70, 1210, 500, 90, { size: 72, color: 'white' });
      b.logo(890, 1180, 130, 130);
    },
  },
  {
    id: 'generic-story',
    family: 'generic',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Genérico — historia', 'Generic — story'),
    build: (b) => {
      b.bg('white');
      gx(b, 'dishName', 60, 290, 960, 270, {
        size: 120,
        color: INK,
        align: 'center',
        maxLines: 2,
        leading: 1.05,
      });
      b.rect(490, 572, 100, 6, { fill: 'primary' });
      b.photo(60, 595, 960, 780, { fit: 'contain', zoom: 0.94, shadow: SOFT });
      gx(b, 'price', 290, 1380, 500, 110, { size: 84, color: 'accentText', align: 'center' });
      b.logo(60, 1396, 130, 130, { onLight: true });
    },
  },
  {
    id: 'generic-delivery',
    family: 'generic',
    slot: 'cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Genérico — banner de delivery', 'Generic — delivery banner'),
    build: (b) => {
      b.bg('white');
      b.logo(60, 40, 120, 120, { onLight: true });
      gx(b, 'dishName', 60, 220, 500, 170, { size: 64, color: INK, maxLines: 2, leading: 1.08 });
      gx(b, 'description', 60, 400, 500, 100, { font: 'body', size: 34, color: MUTED, maxLines: 2 });
      b.rect(60, 525, 90, 5, { fill: 'primary' });
      gx(b, 'price', 60, 560, 500, 100, { size: 72, color: 'accentText' });
      b.photo(580, 40, 600, 720, { fit: 'contain', zoom: 0.94, shadow: SOFT });
    },
  },
  {
    id: 'generic-special',
    family: 'generic',
    slot: 'full',
    category: 'special',
    format: 'ig-square',
    name: bi('Genérico — especial del día', 'Generic — daily special'),
    ad: { headline: esOnly('Especial del día') },
    build: (b) => {
      b.bg('black');
      b.photo(0, 0, 1080, 1080);
      shadeBlack(b, 0, 560, 1080, 520);
      gx(b, 'headline', 70, 770, 700, 50, {
        font: 'body',
        weight: 700,
        size: 38,
        color: 'white',
        caps: true,
        tracking: 3,
      });
      gx(b, 'dishName', 70, 825, 940, 90, { size: 70, color: 'white' });
      gx(b, 'price', 70, 930, 400, 90, { size: 66, color: 'white' });
      b.logo(920, 40, 120, 120);
    },
  },
  {
    id: 'generic-new',
    family: 'generic',
    slot: 'cutout',
    category: 'new',
    format: 'ig-portrait',
    name: bi('Genérico — plato nuevo', 'Generic — new dish'),
    ad: { headline: esOnly('Nuevo') },
    build: (b) => {
      b.bg('white');
      b.rect(80, 80, 220, 70, { fill: 'primary', r: 35 });
      gx(b, 'headline', 80, 80, 220, 70, {
        font: 'body',
        weight: 700,
        size: 40,
        color: 'onPrimarySmall',
        align: 'center',
        valign: 'middle',
      });
      gx(b, 'dishName', 80, 180, 920, 180, { size: 80, color: INK, maxLines: 2, leading: 1.08 });
      b.photo(60, 380, 960, 780, { fit: 'contain', zoom: 0.94, shadow: SOFT });
      gx(b, 'price', 80, 1180, 500, 100, { size: 76, color: 'accentText' });
      b.logo(900, 1190, 130, 130, { onLight: true });
    },
  },
  {
    id: 'generic-promo',
    family: 'generic',
    slot: 'full',
    category: 'promo',
    format: 'story',
    name: bi('Genérico — oferta', 'Generic — offer'),
    build: (b) => {
      b.bg('black');
      b.photo(0, 0, 1080, 1250);
      shadeBlack(b, 0, 850, 1080, 400);
      // A long name wraps to two lines (the M14 long-name check).
      b.rect(90, 1045, 100, 5, { fill: 'primary' });
      gx(b, 'dishName', 90, 1065, 900, 225, { size: 96, color: 'white', maxLines: 2, leading: 1.05 });
      gx(b, 'oldPrice', 90, 1300, 400, 64, { font: 'body', size: 48, color: ['white', 0.75] });
      b.line(90, 1332, 330, 1332, ['white', 0.75], 4);
      gx(b, 'price', 90, 1380, 600, 110, { size: 96, color: 'white' });
      b.logo(890, 290, 130, 130);
    },
  },
  {
    id: 'generic-delivery-dark',
    family: 'generic',
    slot: 'full',
    category: 'delivery',
    format: 'delivery',
    name: bi('Genérico — delivery oscuro', 'Generic — dark delivery banner'),
    build: (b) => {
      b.bg('black');
      b.photo(480, 0, 720, 800);
      shadeBlack(b, 480, 0, 260, 800, 'right');
      b.logo(60, 40, 120, 120);
      gx(b, 'dishName', 60, 200, 460, 170, { size: 62, color: 'white', maxLines: 2, leading: 1.08 });
      b.rect(60, 390, 90, 5, { fill: 'primary' });
      gx(b, 'price', 60, 420, 420, 100, { size: 72, color: 'white' });
    },
  },
];
